import React, { useState } from "react";
import { Button, Form, Input } from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

import logo from "../../assets/img/Logo-png.svg";
import { resetPassword } from "../../api/ApiServices";

const SetNewPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Compute Password Strength
  const calculateStrength = (pwd) => {
    let score = 0;
    if (!pwd) return 0;
    if (pwd.length >= 6) score += 1;
    if (pwd.length >= 8) score += 1;
    if (/[A-Z]/.test(pwd) || /[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return score; // 0 to 4
  };

  const strength = calculateStrength(newPassword);
  const strengthLabels = ["Too Short", "Weak", "Fair", "Good", "Strong"];
  const strengthColors = ["#ef4444", "#f97316", "#eab308", "#3b82f6", "#10b981"];

  const handleReset = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setErrorMsg("");
    setSuccessMsg("");

    if (!email) {
      setErrorMsg("Session expired. Please restart password recovery.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setErrorMsg("Both password fields are required.");
      return;
    }

    if (newPassword.length < 6) {
      setErrorMsg("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("New password and confirm password do not match.");
      return;
    }

    try {
      setLoading(true);
      const res = await resetPassword(email, newPassword, confirmPassword);

      if (res?.success !== false) {
        // Direct redirect without popup
        navigate("/auth/login");
        return;
      } else {
        setErrorMsg(res?.message || "Failed to update password. Please try again.");
      }
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong while resetting your password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-card">
      {/* Header with Logo and Step Badge */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <img src={logo} alt="Area Trek" style={{ height: "36px", maxWidth: "130px", objectFit: "contain" }} />
        <span
          style={{
            background: "#f5f3ff",
            color: "#7c3aed",
            fontSize: "0.75rem",
            fontWeight: "700",
            padding: "4px 12px",
            borderRadius: "20px",
            border: "1px solid #ddd6fe",
            whiteSpace: "nowrap",
          }}
        >
          STEP 3 / 3
        </span>
      </div>

      {/* Lock Emblem */}
      <div className="text-center my-3">
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "16px",
            background: "#f5f3ff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#7c3aed",
            fontSize: "1.4rem",
            border: "1px solid #c4b5fd",
          }}
        >
          <i className="fas fa-lock"></i>
        </div>
      </div>

      <h2
        className="text-center font-weight-bold mb-1"
        style={{ color: "#111827", fontSize: "1.45rem" }}
      >
        Set New Password
      </h2>

      <p
        className="text-center text-muted mb-4"
        style={{ fontSize: "0.85rem", lineHeight: "1.4" }}
      >
        Create a new password for <strong style={{ color: "#111827" }}>{email || "your account"}</strong>.
      </p>

      <Form onSubmit={handleReset}>
        {/* NEW PASSWORD */}
        <div className="custom-form-group mb-3">
          <label className="custom-label" style={{ color: "#4b5563" }}>
            New Password
          </label>
          <div
            className="custom-input-group"
            style={{
              borderColor: errorMsg ? "#f5365c" : "",
            }}
          >
            <i className="fas fa-lock" style={{ color: "#7c3aed" }}></i>
            <Input
              type={showNewPassword ? "text" : "password"}
              placeholder="Min. 6 characters"
              value={newPassword}
              disabled={loading}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setErrorMsg("");
              }}
              autoComplete="new-password"
              required
            />
            <i
              className={showNewPassword ? "fas fa-eye-slash" : "fas fa-eye"}
              onClick={() => !loading && setShowNewPassword((prev) => !prev)}
              style={{
                cursor: loading ? "not-allowed" : "pointer",
                marginLeft: "10px",
                color: "#9ca3af",
              }}
            />
          </div>

          {/* Password Strength Meter */}
          {newPassword.length > 0 && (
            <div className="mt-2 px-1">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: "0.72rem", color: "#6b7280" }}>
                  Strength:{" "}
                  <strong style={{ color: strengthColors[strength] }}>
                    {strengthLabels[strength]}
                  </strong>
                </span>
                <span style={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                  {newPassword.length >= 6 ? "✓ Length OK" : "Min 6 chars"}
                </span>
              </div>
              <div
                style={{
                  height: "4px",
                  background: "#e5e7eb",
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${(strength / 4) * 100}%`,
                    background: strengthColors[strength],
                    transition: "all 0.3s ease",
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* CONFIRM PASSWORD */}
        <div className="custom-form-group mb-4">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="custom-label mb-0" style={{ color: "#4b5563" }}>
              Confirm Password
            </label>
            {confirmPassword.length > 0 && (
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: newPassword === confirmPassword ? "#10b981" : "#ef4444",
                }}
              >
                {newPassword === confirmPassword ? "✓ Match" : "✗ Mismatch"}
              </span>
            )}
          </div>

          <div
            className="custom-input-group"
            style={{
              borderColor:
                confirmPassword.length > 0
                  ? newPassword === confirmPassword
                    ? "#10b981"
                    : "#ef4444"
                  : "",
            }}
          >
            <i className="fas fa-check-double" style={{ color: "#7c3aed" }}></i>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Re-enter new password"
              value={confirmPassword}
              disabled={loading}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrorMsg("");
              }}
              autoComplete="new-password"
              required
            />
            <i
              className={showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"}
              onClick={() => !loading && setShowConfirmPassword((prev) => !prev)}
              style={{
                cursor: loading ? "not-allowed" : "pointer",
                marginLeft: "10px",
                color: "#9ca3af",
              }}
            />
          </div>

          {errorMsg && (
            <div
              style={{
                color: "#f5365c",
                fontSize: "12px",
                marginTop: "6px",
                fontWeight: "500",
              }}
            >
              <i className="fas fa-exclamation-circle mr-1"></i> {errorMsg}
            </div>
          )}
        </div>

        <Button
          className="sign-in-btn"
          type="submit"
          disabled={loading}
          style={{
            background: "linear-gradient(90deg, #7c3aed 0%, #6d28d9 100%)",
          }}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2 gradient-color-loader"></i>
              Updating Password...
            </>
          ) : (
            <>
              Save & Sign In
              <i className="fas fa-arrow-right ml-2"></i>
            </>
          )}
        </Button>

        <div className="text-center mt-4 pt-2">
          <a
            href="/auth/login"
            onClick={(e) => {
              e.preventDefault();
              navigate("/auth/login");
            }}
            style={{
              color: "#6b7280",
              fontSize: "0.85rem",
              fontWeight: "600",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <i className="fas fa-arrow-left mr-2" style={{ fontSize: "0.75rem" }}></i> Back to Sign In
          </a>
        </div>
      </Form>
    </div>
  );
};

export default SetNewPassword;
